import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
// Anonymous consent wiring removed; fallback to local storage only

const BannerContainer = styled.div<{ isVisible: boolean }>`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 20px;
  transform: translateY(${props => props.isVisible ? '0' : '100%'});
  transition: transform 0.3s ease-in-out;
  z-index: 1000;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
`;

const BannerContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const TextContent = styled.div`
  flex: 1;
`;

const Title = styled.h3`
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 600;
`;

const Description = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  opacity: 0.9;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'text' }>`
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: white;
          color: #667eea;
          
          &:hover {
            background: #f8f9ff;
            transform: translateY(-1px);
          }
        `;
      case 'secondary':
        return `
          background: rgba(255, 255, 255, 0.2);
          color: white;
          border: 1px solid rgba(255, 255, 255, 0.3);
          
          &:hover {
            background: rgba(255, 255, 255, 0.3);
          }
        `;
      case 'text':
        return `
          background: transparent;
          color: white;
          text-decoration: underline;
          padding: 10px 8px;
          
          &:hover {
            color: #f0f0f0;
          }
        `;
      default:
        return `
          background: rgba(255, 255, 255, 0.2);
          color: white;
        `;
    }
  }}
`;

const DetailsContainer = styled.div<{ $isOpen: boolean }>`
  max-height: ${props => props.$isOpen ? '400px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
  margin-top: ${props => props.$isOpen ? '16px' : '0'};
`;

const DetailsContent = styled.div`
  padding: 16px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  backdrop-filter: blur(10px);
`;

const CheckboxContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 16px 0;
`;

const CheckboxItem = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
`;

const CheckboxLabel = styled.span`
  flex: 1;
`;

const CheckboxDescription = styled.div`
  font-size: 12px;
  opacity: 0.8;
  margin-left: 24px;
  margin-top: 4px;
`;

interface ConsentData {
  analytics: boolean;
  personalization: boolean;
  marketing: boolean;
}

export const ConsentBanner: React.FC = () => {
  // Keep banner always hidden
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [consent, setConsent] = useState<ConsentData>({
    analytics: false,
    personalization: false,
    marketing: false
  });

  const setUserConsent = (c: any) => {
    try { localStorage.setItem('user_consent', JSON.stringify(c)); } catch {}
  };

  useEffect(() => {
    // Automatically accept all cookies for all users
    const fullConsent = {
      analytics: true,
      personalization: true,
      marketing: true
    };
    
    // Set consent in context
    setUserConsent(fullConsent);
    
    // Set consent in localStorage for persistence
    localStorage.setItem('user_consent', JSON.stringify(fullConsent));
    
    // Ensure banner stays hidden
    setIsVisible(false);
  }, [setUserConsent]);

  const handleConsentChange = (key: keyof ConsentData, value: boolean) => {
    setConsent(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleAcceptAll = () => {
    const fullConsent = {
      analytics: true,
      personalization: true,
      marketing: true
    };
    
    setUserConsent(fullConsent);
    setIsVisible(false);
  };

  const handleAcceptSelected = () => {
    setUserConsent(consent);
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    const noConsent = {
      analytics: false,
      personalization: false,
      marketing: false
    };
    
    setUserConsent(noConsent);
    setIsVisible(false);
  };

  const handleAcceptEssential = () => {
    const essentialConsent = {
      analytics: true, // Considered essential for functionality
      personalization: false,
      marketing: false
    };
    
    setUserConsent(essentialConsent);
    setIsVisible(false);
  };

  // Always return null to keep banner hidden
  return null;

  // Original banner code kept but commented out
  /*
  if (!isVisible) return null;

  return (
    <BannerContainer isVisible={isVisible}>
      <BannerContent>
        <TextContent>
          <Title>🍪 Privacy & Cookies</Title>
          <Description>
            We use cookies and similar technologies to enhance your browsing experience, 
            analyze site traffic, and personalize content. Your privacy matters to us.
          </Description>
        </TextContent>
        
        <ButtonContainer>
          <Button variant="text" onClick={() => setShowDetails(!showDetails)}>
            {showDetails ? 'Hide Details' : 'Customize'}
          </Button>
          <Button variant="secondary" onClick={handleAcceptEssential}>
            Essential Only
          </Button>
          <Button variant="primary" onClick={handleAcceptAll}>
            Accept All
          </Button>
        </ButtonContainer>
      </BannerContent>

      <DetailsContainer $isOpen={showDetails}>
        <DetailsContent>
          <Title style={{ fontSize: '16px', marginBottom: '12px' }}>
            Cookie Preferences
          </Title>
          
          <CheckboxContainer>
            <CheckboxItem>
              <Checkbox
                type="checkbox"
                checked={consent.analytics}
                onChange={(e) => handleConsentChange('analytics', e.target.checked)}
              />
              <CheckboxLabel>Analytics & Performance</CheckboxLabel>
            </CheckboxItem>
            <CheckboxDescription>
              Help us understand how visitors interact with our website by collecting 
              and reporting information anonymously.
            </CheckboxDescription>

            <CheckboxItem>
              <Checkbox
                type="checkbox"
                checked={consent.personalization}
                onChange={(e) => handleConsentChange('personalization', e.target.checked)}
              />
              <CheckboxLabel>Personalization</CheckboxLabel>
            </CheckboxItem>
            <CheckboxDescription>
              Remember your preferences and provide customized content and features 
              based on your usage patterns.
            </CheckboxDescription>

            <CheckboxItem>
              <Checkbox
                type="checkbox"
                checked={consent.marketing}
                onChange={(e) => handleConsentChange('marketing', e.target.checked)}
              />
              <CheckboxLabel>Marketing & Advertising</CheckboxLabel>
            </CheckboxItem>
            <CheckboxDescription>
              Show you relevant advertisements and promotional content based on your 
              interests and browsing behavior.
            </CheckboxDescription>
          </CheckboxContainer>

          <ButtonContainer style={{ marginTop: '16px' }}>
            <Button variant="secondary" onClick={handleRejectAll}>
              Reject All
            </Button>
            <Button variant="primary" onClick={handleAcceptSelected}>
              Save Preferences
            </Button>
          </ButtonContainer>
        </DetailsContent>
      </DetailsContainer>
    </BannerContainer>
  );
  */
}; 